"use client"

import { Trash01 } from "@untitledui/icons"
import { type KeyboardEvent, useState } from "react"

import { Avatar } from "@/components/ui/avatar"
import { ButtonUtility } from "@/components/ui/button-utility"
import { cn } from "@/lib/utils"

const MAX_NOTE_LENGTH = 280

const marginVoice = {
  fontFamily: '"Newsreader", Georgia, serif',
  fontVariationSettings: '"opsz" 12, "wght" 400',
}

interface FieldNoteProps {
  /** Who left the note — the coordinator's name (avatar alt + the note's aria-label). */
  authorName: string
  /** The coordinator's avatar URL; falls back to a name-seeded beam when absent. */
  avatarUrl?: string | null
  /** The note body. */
  body: string
  /** The value the field was seeded with. */
  seededValue: string
  /** The field's current value. When it diverges, the note is "addressed". */
  currentValue: string
}

/**
 * A private "margin note" in the warm-start claim editor: the coordinator's
 * marginalia, anchored to a field, that quietly **self-dims** the moment the
 * field diverges from what was seeded. Pure derived state — never persisted (a
 * stored boolean would drift from the live diff).
 *
 * Identity is **shown, not told**: a small avatar carries *who* left the note
 * (the name lives in its alt text + the container's aria-label), so the body
 * isn't prefixed with an awkward full-name label. The body keeps its Newsreader
 * red-pencil voice — set in Newsreader italic with the optical size pinned low
 * (`opsz` 12, a softer/warmer cut), inked in the warm Red Pencil tokens. The
 * dim is colour + a slight opacity step, gated behind `prefers-reduced-motion`;
 * the "· done" marker stays in the same italic voice.
 */
export function FieldNote({
  authorName,
  avatarUrl,
  body,
  seededValue,
  currentValue,
}: FieldNoteProps) {
  const addressed = currentValue.trim() !== seededValue.trim()

  return (
    <div
      data-state={addressed ? "done" : "active"}
      aria-label={`Note from ${authorName}`}
      className={cn(
        "flex items-start gap-2.5 transition-opacity duration-200 motion-reduce:transition-none",
        addressed && "opacity-70",
      )}
    >
      <Avatar
        name={authorName}
        avatarUrl={avatarUrl}
        size="xs"
        className="mt-0.5 shrink-0"
      />
      <p
        style={marginVoice}
        className={cn(
          "max-w-[46ch] text-[0.9375rem] leading-snug italic transition-colors duration-200 motion-reduce:transition-none",
          addressed
            ? "text-[color:var(--ink-margin-dim)]"
            : "text-[color:var(--ink-margin)]",
        )}
      >
        {body}
        {addressed && " · done"}
      </p>
    </div>
  )
}

interface FieldNoteEditorProps {
  /** Who the note reads as — the coordinator (the signed-in admin). */
  authorName: string
  avatarUrl?: string | null
  /** The persisted note body; empty for a note being composed. */
  body: string
  /** Fired on blur when the (trimmed) body changed and is non-empty. */
  onCommit: (body: string) => void
  /** Fired when the note is removed — the trash affordance or blur-empty. */
  onRemove: () => void
  /** Focus the surface on mount — for the just-added, still-empty note. */
  autoFocus?: boolean
}

/**
 * The coordinator's editable margin note: the same avatar-led Newsreader
 * red-pencil voice as {@link FieldNote}, but the body is an edit surface —
 * an auto-sizing textarea with the baseline-rule/hover-ground/focus-ring
 * signifiers of `EditableText`, committing on blur. Escape reverts to the
 * persisted body; emptying the note and blurring removes it. The 280-char
 * cap surfaces as a quiet counter only once it's nearly spent.
 */
export function FieldNoteEditor({
  authorName,
  avatarUrl,
  body,
  onCommit,
  onRemove,
  autoFocus = false,
}: FieldNoteEditorProps) {
  const [draft, setDraft] = useState(body)
  // Re-adopt the persisted body when it changes underneath us (a queued save
  // landing) — the documented "adjust state when a prop changes" pattern.
  const [prevBody, setPrevBody] = useState(body)
  if (body !== prevBody) {
    setPrevBody(body)
    setDraft(body)
  }

  const remaining = MAX_NOTE_LENGTH - draft.length

  const handleBlur = () => {
    const next = draft.trim()
    // Blurring an emptied note removes it — and collapses a still-unwritten
    // one (the composing case, where `body` is already empty).
    if (next === "") {
      onRemove()
      return
    }
    if (next === body.trim()) return
    onCommit(next)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      setDraft(body)
      e.currentTarget.blur()
    }
  }

  return (
    <div
      aria-label={`Your note, as ${authorName}`}
      className="group/note flex items-start gap-2.5"
    >
      <Avatar
        name={authorName}
        avatarUrl={avatarUrl}
        size="xs"
        className="mt-0.5 shrink-0"
      />
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "-mx-2 rounded-md px-2 py-1",
            "border-secondary border-b transition-colors duration-150 motion-reduce:transition-none",
            "hover:bg-surface-grouped/60",
            "focus-within:bg-surface-grouped focus-within:border-transparent focus-within:ring-2 focus-within:ring-[color:var(--color-focus-ring)] focus-within:ring-inset",
          )}
        >
          <textarea
            aria-label="Margin note"
            value={draft}
            rows={1}
            maxLength={MAX_NOTE_LENGTH}
            placeholder="A private nudge for them…"
            autoFocus={autoFocus}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            style={marginVoice}
            className={cn(
              "placeholder:text-placeholder block [field-sizing:content] w-full resize-none border-0 bg-transparent p-0 outline-none focus:ring-0",
              "max-w-[46ch] text-[0.9375rem] leading-snug italic",
              "text-[color:var(--ink-margin)]",
            )}
          />
        </div>
        {remaining <= 40 && (
          <p className="text-tertiary mt-1 text-xs tabular-nums">
            {remaining} left
          </p>
        )}
      </div>
      {body.trim() !== "" && (
        <ButtonUtility
          size="xs"
          color="tertiary"
          tooltip="Remove note"
          icon={Trash01}
          onClick={onRemove}
          className="opacity-0 transition-opacity group-focus-within/note:opacity-100 group-hover/note:opacity-100 motion-reduce:transition-none"
        />
      )}
    </div>
  )
}
